import { LabeledInfo as OutboundInfo } from "./LabeledInfo";
import {
  AddOutlined,
  AssignmentTurnedInOutlined,
  CheckCircleOutline,
  DeleteOutline,
  ExpandLess,
  ExpandMore,
  InventoryOutlined,
  LocalShippingOutlined,
  FactCheckOutlined,
  PersonOutline,
  RefreshOutlined,
  ScienceOutlined,
  SearchOutlined,
  WarningAmberOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CreateOutboundOrder,
  OutboundOrder,
  PortalAsnScenario,
  QualityInspection,
  acceptOutboundOrder,
  allocateOutboundOrderLine,
  cancelPortalOutboundOrder,
  createPortalAsnSimulation,
  createPortalOutboundOrder,
  loadOutboundOrders,
  loadPortalDerogations,
  loadPortalOutboundCatalog,
  loadPortalAsnSimulations,
  loadPortalOutboundOrders
} from "./api";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { PageHeader } from "./PageHeader";
import { HomeDashboardSelector } from "./HomeDashboardSelector";
import { PortalDerogationInbox } from "./PortalDerogationInbox";

function locale(language: string) {
  return language.startsWith("en") ? "en-US" : "fr-FR";
}

function formatQuantity(value: number, language: string) {
  return new Intl.NumberFormat(locale(language), { maximumFractionDigits: 3 }).format(value);
}

function formatMoment(value: string | null, language: string) {
  if (!value) return null;
  return new Date(value).toLocaleString(locale(language), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function isOutboundOrderFinished(order: OutboundOrder) {
  return order.status === "CANCELLED" || order.milestones.some((milestone) =>
    milestone.code === "DELIVERY" && milestone.state === "COMPLETED"
  );
}

export function OutboundOrderPage({
  onBack, permissions, initialSearch = "", initialTargetId = null,
  onOpenReference
}: {
  onBack: () => void;
  permissions: string[];
  initialSearch?: string;
  initialTargetId?: string | null;
  onOpenReference?: (
    reference: OutboundOrder["milestones"][number]["references"][number]
  ) => void;
}) {
  const { t, i18n } = useTranslation();
  const query = useQuery({
    queryKey: ["outbound-orders", "internal"],
    queryFn: loadOutboundOrders
  });
  const [search, setSearch] = useState(initialSearch);
  const [lifecycleTab, setLifecycleTab] = useState<"open" | "finished">("open");
  useEffect(() => setSearch(initialSearch), [initialSearch]);
  useEffect(() => {
    if (!initialTargetId || !query.data) return;
    const target = query.data.find((order) => order.id === initialTargetId);
    if (target) setLifecycleTab(isOutboundOrderFinished(target)
      ? "finished" : "open");
  }, [initialTargetId, query.data]);
  const lifecycleCounts = useMemo(() => {
    const orders = query.data ?? [];
    return {
      open: orders.filter((order) => !isOutboundOrderFinished(order)).length,
      finished: orders.filter(isOutboundOrderFinished).length
    };
  }, [query.data]);
  const visibleOrders = useMemo(() => {
    const orders = query.data ?? [];
    if (initialTargetId) {
      return orders.filter((order) => order.id === initialTargetId);
    }
    const lifecycleOrders = orders.filter((order) => lifecycleTab === "finished"
      ? isOutboundOrderFinished(order) : !isOutboundOrderFinished(order));
    const normalizedSearch = search.trim().toLocaleLowerCase("fr");
    if (!normalizedSearch) return lifecycleOrders;
    return lifecycleOrders.filter((order) =>
      [
        order.orderNumber,
        order.externalReference,
        order.ownerClientCode,
        order.recipientClientCode,
        order.warehouseCode,
        order.destinationCode,
        order.destinationName,
        order.status,
        ...order.lines.flatMap((line) => [
          line.itemCode,
          line.itemName,
          line.status
        ])
      ].some((value) => (value ?? "").toLocaleLowerCase("fr")
        .includes(normalizedSearch))
    );
  }, [initialTargetId, lifecycleTab, query.data, search]);

  return (
    <Stack spacing={1.5} sx={{
      height: { xs: "100%", sm: "auto", md: "100%" }, minHeight: 0,
      overflow: { xs: "hidden", sm: "visible", md: "hidden" }
    }}>
      <MobileOperationalHeader moduleLabel={t("outboundOrder.title")}
        search={search} onSearchChange={setSearch}
        searchPlaceholder={t("outboundOrder.search.placeholder")} onBack={onBack} />
      <Box sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}>
        <PageHeader title={t("outboundOrder.title")} compact onBack={onBack} />
      </Box>
      <Tabs value={lifecycleTab}
        onChange={(_, value) => setLifecycleTab(value)}
        aria-label={t("outboundOrder.lifecycleAria")}>
        <Tab value="open" label={`${t("outboundOrder.tabs.open")} (${lifecycleCounts.open})`} />
        <Tab value="finished"
          label={`${t("outboundOrder.tabs.finished")} (${lifecycleCounts.finished})`} />
      </Tabs>
      <Card variant="outlined" sx={{ display: { xs: "none", sm: "block" } }}>
        <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
          <TextField fullWidth size="small" label={t("common.filters.search")}
            placeholder={t("outboundOrder.search.placeholder")}
            value={search} onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: <SearchOutlined color="action" sx={{ mr: 1 }} />
            }} />
        </CardContent>
      </Card>
      <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", pr: .5 }}>
        {query.isError && (
          <Alert severity="error">
            {t("outboundOrder.errors.load")}
          </Alert>
        )}
        {!query.isLoading && !query.isError && !visibleOrders.length && (
          <Alert severity="info">
            {search
              ? t("outboundOrder.empty.filtered", { search })
              : t("outboundOrder.empty.view")}
          </Alert>
        )}
        <Stack spacing={1.25}>
          {visibleOrders.map((order) => (
            <OutboundOrderCard key={order.id} order={order}
              internal permissions={permissions}
              onOpenReference={onOpenReference}
              initiallyExpanded={order.id === initialTargetId} />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

export function CustomerOutboundDashboard({
  permissions, onShowStockRisk, onShowStockRiskV2, onShowReplenishment
}: {
  permissions: string[];
  onShowStockRisk?: () => void;
  onShowStockRiskV2?: () => void;
  onShowReplenishment?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const canReadOutbound = permissions.includes("outbound_order:self:read");
  const canReadDerogations = permissions.includes("derogation:self:read");
  const [tab, setTab] = useState<"overview" | "requests" | "simulator" | "derogations">("overview");
  const [targetDerogationId, setTargetDerogationId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const canSimulateAsn = permissions.includes("asn_simulation:self:create");
  const query = useQuery({
    queryKey: ["outbound-orders", "portal"],
    queryFn: loadPortalOutboundOrders,
    enabled: canReadOutbound
  });
  const derogationsQuery = useQuery({
    queryKey: ["portal-derogations"], queryFn: loadPortalDerogations,
    enabled: canReadDerogations
  });
  const orders = query.data ?? [];
  const portalDerogations = derogationsQuery.data ?? [];
  const clientName = portalDerogations.find((entry) => entry.clientName)?.clientName
    ?? orders.find((order) => order.recipientClientName)?.recipientClientName
    ?? t("outboundOrder.portal.cockpit.customerFallback");
  const updatedAt = Math.max(query.dataUpdatedAt, derogationsQuery.dataUpdatedAt || 0);
  const updatedTime = new Date(updatedAt || Date.now()).toLocaleTimeString(
    locale(i18n.resolvedLanguage ?? "en"), { hour: "2-digit", minute: "2-digit" }
  );
  const refresh = () => void Promise.all([
    query.refetch(),
    canReadDerogations ? derogationsQuery.refetch() : Promise.resolve()
  ]);

  return (
    <Stack spacing={1.1} sx={{ height: "100%", minHeight: 0, width: "100%" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between">
        <Box>
          <Typography variant="h4" component="h1" fontWeight={850}
            sx={{ fontSize: { xs: "1.4rem", md: "1.75rem" } }}>
            {t("outboundOrder.portal.cockpit.title", { name: clientName })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("outboundOrder.portal.cockpit.subtitle")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {(permissions.includes("stock_risk:read") || permissions.includes("stock_risk_v2:read") || permissions.includes("replenishment:read")) && <HomeDashboardSelector
            value="operations" operationsLabel={t("outboundOrder.portal.cockpit.appBarTitle")}
            showStockRisk={false}
            showStockRiskV2={permissions.includes("stock_risk_v2:read")}
            showReplenishment={permissions.includes("replenishment:read")}
            showExpiryRisk={permissions.includes("expiry_risk:read")}
            onChange={(view) => view === "stock-risk" ? onShowStockRisk?.()
              : view === "stock-risk-v2" ? onShowStockRiskV2?.()
              : view === "replenishment" && onShowReplenishment?.()} />}
          <IconButton size="small" aria-label={t("dashboard.refresh")}
            onClick={refresh}><RefreshOutlined /></IconButton>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {t("dashboard.updated", { time: updatedTime })}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1}
        alignItems={{ xs: "stretch", md: "center" }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}
          variant="scrollable" scrollButtons="auto"
          aria-label={t("outboundOrder.portal.aria")} sx={{ flex: 1, minWidth: 0 }}>
          <Tab value="overview" icon={<AssignmentTurnedInOutlined />}
            iconPosition="start" label={t("outboundOrder.portal.overview")} />
          {canReadOutbound && <Tab value="requests" icon={<LocalShippingOutlined />}
            iconPosition="start" label={t("outboundOrder.portal.requests")} />}
          {canSimulateAsn && (
            <Tab value="simulator" icon={<ScienceOutlined />} iconPosition="start"
              label={t("outboundOrder.portal.simulator")} />
          )}
          {canReadDerogations && (
            <Tab value="derogations" icon={<FactCheckOutlined />}
              iconPosition="start" label={t("quality.derogations.module")} />
          )}
        </Tabs>
        {tab === "requests" && permissions.includes("outbound_order:self:create") && <Button
          variant="contained" startIcon={<AddOutlined />}
          onClick={() => setCreateOpen(true)} sx={{ flexShrink: 0 }}>
          {t("outboundOrder.portal.newRequest")}
        </Button>}
      </Stack>
      {tab === "overview" ? <CustomerCockpitOverview permissions={permissions}
        orders={orders} ordersLoading={query.isLoading} ordersError={query.isError}
        derogations={portalDerogations}
        derogationsLoading={derogationsQuery.isLoading}
        derogationsError={derogationsQuery.isError}
        onOpenRequests={() => setTab("requests")}
        onOpenDerogations={() => setTab("derogations")}
        onOpenDerogation={(inspection) => {
          setTargetDerogationId(inspection.id);
          setTab("derogations");
        }} />
      : tab === "requests" ? <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", pr: .5 }}>
        {query.isError && (
          <Alert severity="error">
            {t("outboundOrder.errors.portalLoad")}
          </Alert>
        )}
        {!query.isLoading && !query.isError && !orders.length && (
          <Alert severity="info">
            {t("outboundOrder.empty.portal")}
          </Alert>
        )}
        <Stack spacing={1.25}>
          {orders.map((order) => (
            <OutboundOrderCard key={order.id} order={order}
              internal={false} permissions={permissions} />
          ))}
        </Stack>
      </Box> : tab === "simulator" && canSimulateAsn
        ? <PortalAsnSimulator />
        : tab === "derogations"
          ? <PortalDerogationInbox canDecide={permissions.includes(
            "derogation:self:decide"
          )} initialInspectionId={targetDerogationId}
            onInitialInspectionConsumed={() => setTargetDerogationId(null)} /> : null}
      <CreateOutboundOrderDialog open={createOpen}
        onClose={() => setCreateOpen(false)} />
    </Stack>
  );
}

function CustomerCockpitOverview({
  permissions, orders, ordersLoading, ordersError, derogations,
  derogationsLoading, derogationsError, onOpenRequests, onOpenDerogations,
  onOpenDerogation
}: {
  permissions: string[];
  orders: OutboundOrder[];
  ordersLoading: boolean;
  ordersError: boolean;
  derogations: QualityInspection[];
  derogationsLoading: boolean;
  derogationsError: boolean;
  onOpenRequests: () => void;
  onOpenDerogations: () => void;
  onOpenDerogation: (inspection: QualityInspection) => void;
}) {
  const { t, i18n } = useTranslation();
  const canDecideDerogations = permissions.includes("derogation:self:decide");
  const decisionsRequired = derogations.filter((entry) =>
    entry.workflowState === "PARTNER_REVIEW" && canDecideDerogations);
  const openOrders = orders.filter((order) => !isOutboundOrderFinished(order));
  const deliveriesInProgress = openOrders.filter((order) => order.milestones.some(
    (milestone) => milestone.code === "PREPARATION" || milestone.code === "DELIVERY"
      ? milestone.state === "CURRENT" || milestone.state === "COMPLETED" : false
  ));
  const orderAlerts = openOrders.filter((order) => order.status === "ATP_FAILED"
    || order.milestones.some((milestone) => milestone.state === "ERROR"));
  const recentOrders = [...orders].sort((left, right) =>
    right.submittedAt.localeCompare(left.submittedAt)).slice(0, 3);
  const loading = ordersLoading || derogationsLoading;
  const error = ordersError || derogationsError;

  const metric = (label: string, value: number, detail: string,
    color: "primary" | "success" | "warning" | "error",
    icon: ReactNode, action: () => void, disabled = false) => (
    <Card variant="outlined" component="button" onClick={action}
      title={detail}
      disabled={disabled} sx={{ textAlign: "left", color: "text.primary",
        bgcolor: "background.paper", px: 1.5, py: .8,
        borderTop: 3, borderColor: `${color}.main`,
        cursor: disabled ? "default" : "pointer", minWidth: 0,
        "&:hover": disabled ? {} : { bgcolor: "action.hover" } }}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box sx={{ color: `${color}.main`, display: "flex" }}>{icon}</Box>
        <Box>
          <Typography variant="h5" fontWeight={850} lineHeight={1}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      </Stack>
    </Card>
  );

  return <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", pr: .5 }}>
    <Stack spacing={1.5}>
      {error && <Alert severity="error">{t("outboundOrder.portal.cockpit.loadError")}</Alert>}
      <Box sx={{ display: "grid", gridTemplateColumns: {
        xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))"
      }, gap: 1.25 }}>
        {metric(t("outboundOrder.portal.cockpit.metrics.actions"), decisionsRequired.length,
          t("outboundOrder.portal.cockpit.metrics.actionsDetail", { count: decisionsRequired.length }),
          decisionsRequired.length ? "warning" : "success",
          <AssignmentTurnedInOutlined />, onOpenDerogations,
          !permissions.includes("derogation:self:read"))}
        {metric(t("outboundOrder.portal.cockpit.metrics.requests"), openOrders.length,
          t("outboundOrder.portal.cockpit.metrics.requestsDetail"), "primary",
          <LocalShippingOutlined />, onOpenRequests)}
        {metric(t("outboundOrder.portal.cockpit.metrics.deliveries"), deliveriesInProgress.length,
          t("outboundOrder.portal.cockpit.metrics.deliveriesDetail"), "success",
          <InventoryOutlined />, onOpenRequests)}
        {metric(t("outboundOrder.portal.cockpit.metrics.alerts"), orderAlerts.length,
          t("outboundOrder.portal.cockpit.metrics.alertsDetail"),
          "error", <WarningAmberOutlined />, onOpenRequests)}
      </Box>
      {loading ? <Alert severity="info">{t("outboundOrder.portal.cockpit.loading")}</Alert> : <Box sx={{
        display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.45fr) minmax(280px, .75fr)" }, gap: 1.5
      }}>
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" fontWeight={850}>{t("outboundOrder.portal.cockpit.priorityTitle")}</Typography>
              {permissions.includes("derogation:self:read") && <Button size="small"
                onClick={onOpenDerogations}>{t("outboundOrder.portal.cockpit.showAll")}</Button>}
            </Stack>
            {!decisionsRequired.length ? <Alert severity="success">
              {t("outboundOrder.portal.cockpit.noAction")}
            </Alert> : <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
              {decisionsRequired.slice(0, 4).map((entry) => <Stack key={entry.id}
                direction={{ xs: "column", sm: "row" }} spacing={1.25}
                alignItems={{ sm: "center" }} sx={{ py: 1.25 }}>
                <Box sx={{ color: "warning.main" }}><WarningAmberOutlined /></Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack spacing={1}>
                    <OutboundInfo label={t("quality.derogationCard.reference")}>{entry.inspectionNumber}</OutboundInfo>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={2} rowGap={1}>
                      <OutboundInfo label={t("quality.derogationCard.item")} prominent>{entry.itemCodes}</OutboundInfo>
                      <OutboundInfo label={t("quality.derogationCard.quantity")} prominent>{entry.quantity} {entry.uom}</OutboundInfo>
                      <OutboundInfo label={t("quality.portalView.requestedExpiry")}>{entry.requestedExpirationDate ?? "—"}</OutboundInfo>
                    </Stack>
                  </Stack>
                </Box>
                <Chip size="small" color="warning" label={t("quality.portal.toDecide")} />
                <Button variant="contained" size="small"
                  onClick={() => onOpenDerogation(entry)}>
                  {t("quality.actions.decide")}
                </Button>
              </Stack>)}
            </Stack>}
          </CardContent>
        </Card>
        <Stack spacing={1.5}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={850}>{t("outboundOrder.portal.cockpit.flowTitle")}</Typography>
                <Button size="small" onClick={onOpenRequests}>{t("outboundOrder.portal.cockpit.details")}</Button>
              </Stack>
              <Stack spacing={1.1}>
                {[
                  ["submitted", openOrders.filter((order) => order.status === "SUBMITTED").length],
                  ["available", openOrders.filter((order) => ["ATP_AVAILABLE", "PARTIALLY_ALLOCATED"].includes(order.status)).length],
                  ["allocated", openOrders.filter((order) => order.status === "ALLOCATED").length],
                  ["delivery", deliveriesInProgress.length]
                ].map(([key, value]) => <Box key={key}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={700}>{t(`outboundOrder.portal.cockpit.flow.${key}`)}</Typography>
                    <Typography variant="body2" color="text.secondary">{value}</Typography>
                  </Stack>
                  <Box sx={{ mt: .5, height: 7, bgcolor: "action.hover", borderRadius: 9, overflow: "hidden" }}>
                    <Box sx={{ width: `${openOrders.length ? Math.max(8, Number(value) / openOrders.length * 100) : 0}%`, height: "100%", bgcolor: "success.main" }} />
                  </Box>
                </Box>)}
              </Stack>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight={850} sx={{ mb: 1 }}>{t("outboundOrder.portal.cockpit.recentTitle")}</Typography>
              {!recentOrders.length ? <Typography color="text.secondary">{t("outboundOrder.empty.portal")}</Typography>
                : <Stack spacing={1}>{recentOrders.map((order) => <Box key={order.id}>
                  <Typography variant="body2" fontWeight={800}>{order.orderNumber}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatMoment(order.submittedAt, i18n.resolvedLanguage ?? "en")} · {t(`outboundOrder.status.${order.status}`)}
                  </Typography>
                </Box>)}</Stack>}
            </CardContent>
          </Card>
        </Stack>
      </Box>}
    </Stack>
  </Box>;
}

const portalAsnScenarios: PortalAsnScenario[] = [
  "STANDARD", "MULTI_LINE", "LOT", "SERIALIZED", "CUSTOMS"
];

function PortalAsnSimulator() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [customsClearanceRequired, setCustomsClearanceRequired] = useState(false);
  const query = useQuery({
    queryKey: ["asn-simulations", "portal"],
    queryFn: loadPortalAsnSimulations
  });
  const create = useMutation({
    mutationFn: createPortalAsnSimulation,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ["asn-simulations", "portal"]
    })
  });

  return (
    <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", pr: .5 }}>
      <Stack spacing={2}>
        <Alert severity="info" icon={<ScienceOutlined />}>
          {t("outboundOrder.simulator.info")}
        </Alert>
        <FormControlLabel
          control={<Switch checked={customsClearanceRequired}
            onChange={(event) => setCustomsClearanceRequired(
              event.target.checked
            )} />}
          label={t("outboundOrder.simulator.customsToggle")}
        />
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 1.5
        }}>
          {portalAsnScenarios.map((scenario) => (
            <Card key={scenario} variant="outlined">
              <CardContent sx={{ height: "100%", display: "flex",
                flexDirection: "column", gap: 1 }}>
                <Typography variant="h6" fontWeight={850}>
                  {t(`outboundOrder.simulator.scenarios.${scenario}.title`)}
                </Typography>
                <Typography color="text.secondary" sx={{ flexGrow: 1 }}>
                  {t(`outboundOrder.simulator.scenarios.${scenario}.description`)}
                </Typography>
                <Button variant="contained" startIcon={<ScienceOutlined />}
                  disabled={create.isPending}
                  onClick={() => create.mutate({
                    scenario,
                    customsClearanceRequired:
                      scenario === "CUSTOMS" || customsClearanceRequired
                  })}>
                  {t("outboundOrder.simulator.generate")}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
        {create.isError && (
          <Alert severity="error">
            {t("outboundOrder.simulator.generateError")}
          </Alert>
        )}
        <Typography variant="h6" fontWeight={850}>
          {t("outboundOrder.simulator.latest")}
        </Typography>
        {query.isError && (
          <Alert severity="error">{t("outboundOrder.simulator.loadError")}</Alert>
        )}
        {!query.isLoading && !query.isError && !query.data?.length && (
          <Alert severity="info">{t("outboundOrder.simulator.empty")}</Alert>
        )}
        {(query.data ?? []).map((asn) => (
          <Card key={asn.id} variant="outlined">
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                alignItems={{ sm: "center" }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={850}>
                    {asn.asnNumber} · {asn.supplierName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {asn.warehouseCode} · {t("outboundOrder.simulator.created", {
                      count: asn.lineCount,
                      date: new Date(asn.createdAt).toLocaleString(locale(i18n.language))
                    })}
                  </Typography>
                </Box>
                <Chip label={t("outboundOrder.simulator.test")} color="warning" variant="outlined" />
                {asn.customsClearanceRequired && (
                  <Chip label={asn.customsClearanceNumber
                    ? `${asn.customsClearanceNumber} · ${t("outboundOrder.simulator.customs")}`
                    : t("outboundOrder.simulator.customsRequired")} color="warning" variant="outlined" />
                )}
                <Chip label={asn.status === "CUSTOMS_PENDING"
                  ? t("outboundOrder.simulator.customs") : asn.status}
                  color={asn.status === "CUSTOMS_PENDING" ? "warning" : "success"} />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

function OutboundOrderCard({
  order, internal, permissions, initiallyExpanded = false, onOpenReference
}: {
  order: OutboundOrder;
  internal: boolean;
  permissions: string[];
  initiallyExpanded?: boolean;
  onOpenReference?: (
    reference: OutboundOrder["milestones"][number]["references"][number]
  ) => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(initiallyExpanded);
  useEffect(() => {
    if (initiallyExpanded) setExpanded(true);
  }, [initiallyExpanded]);
  const accept = useMutation({
    mutationFn: () => acceptOutboundOrder(order),
    onSuccess: () => {
      setExpanded(true);
      return queryClient.invalidateQueries({
        queryKey: ["outbound-orders"]
      });
    }
  });
  const allocate = useMutation({
    mutationFn: (lineId: string) => {
      const line = order.lines.find((entry) => entry.id === lineId);
      if (!line) throw new Error(t("outboundOrder.errors.lineMissing"));
      return allocateOutboundOrderLine(order, line);
    },
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ["outbound-orders"]
    })
  });
  const cancel = useMutation({
    mutationFn: () => cancelPortalOutboundOrder(
      order, t("outboundOrder.card.cancelReason")
    ),
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ["outbound-orders"]
    })
  });
  let displayStatus = t(`outboundOrder.status.${order.status}`);
  let tone: "error" | "success" | "warning" | "info" =
    order.status === "ATP_FAILED" ? "error"
    : order.status === "ALLOCATED" ? "success"
      : order.status === "SUBMITTED" ? "warning" : "info";
  const delivery = order.milestones.find(
    (milestone) => milestone.code === "DELIVERY"
  );
  const preparation = order.milestones.find(
    (milestone) => milestone.code === "PREPARATION"
  );
  if (delivery?.state === "COMPLETED") {
    displayStatus = t("outboundOrder.status.delivered");
    tone = "success";
  } else if (delivery?.state === "CURRENT") {
    displayStatus = t("outboundOrder.status.deliveryProgress");
    tone = "info";
  } else if (preparation?.state === "COMPLETED") {
    displayStatus = t("outboundOrder.status.prepared");
    tone = "success";
  } else if (preparation?.state === "CURRENT") {
    displayStatus = preparation.detailCode === "PREPARATION_PLANNED"
      ? t("outboundOrder.status.toPrepare") : t("outboundOrder.status.preparationProgress");
    tone = "info";
  }
  const currentMilestoneIndex = order.milestones.findIndex(
    (milestone) => ["CURRENT", "ERROR"].includes(milestone.state)
  );
  const lastCompletedMilestoneIndex = order.milestones.reduce(
    (last, milestone, index) =>
      milestone.state === "COMPLETED" ? index : last,
    -1
  );
  const activeMilestoneIndex = Math.max(
    0,
    currentMilestoneIndex >= 0
      ? currentMilestoneIndex : lastCompletedMilestoneIndex
  );
  const milestoneLabel = (code: string, fallback: string) =>
    t(`outboundOrder.milestones.labels.${code}`, { defaultValue: fallback });
  const milestoneDetail = (milestone: OutboundOrder["milestones"][number]) => {
    if (milestone.code === "SUBMITTED") return t("outboundOrder.milestones.registered");
    if (milestone.code === "ATP") {
      if (milestone.state === "ERROR") return t("outboundOrder.milestones.stockUnavailable");
      return milestone.occurredAt
        ? t("outboundOrder.milestones.stockAvailable")
        : t("outboundOrder.milestones.checkRequired");
    }
    if (milestone.code === "ALLOCATED") {
      const [allocated = "0", total = "0"] = milestone.detail.match(/\d+/g) ?? [];
      return t("outboundOrder.milestones.allocatedLines", { allocated, total });
    }
    if (milestone.code === "PREPARATION") {
      if (milestone.state === "COMPLETED") return t("outboundOrder.milestones.preparationCompleted");
      if (milestone.detailCode === "PICKING_IN_PROGRESS") return t("outboundOrder.milestones.pickingInProgress");
      if (milestone.state === "CURRENT") return t("outboundOrder.milestones.preparationPlanned");
      return t("outboundOrder.milestones.upcoming");
    }
    if (milestone.code === "DELIVERY") {
      if (milestone.state === "COMPLETED") return t("outboundOrder.milestones.delivered");
      if (milestone.detailCode === "DELIVERY_IN_PROGRESS") return t("outboundOrder.milestones.deliveryInProgress");
      if (milestone.state === "CURRENT") return t("outboundOrder.milestones.missionPlanned");
      return t("outboundOrder.milestones.upcoming");
    }
    return milestone.detail;
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}
          alignItems={{ md: "center" }}>
          {internal ? <InventoryOutlined color="secondary" />
            : <PersonOutline color="secondary" />}
          <Stack spacing={1} sx={{ minWidth: 0, flexGrow: 1 }}>
            <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
              <OutboundInfo label={t("outboundOrder.card.fields.order")} prominent>
                {order.orderNumber}
              </OutboundInfo>
              <OutboundInfo label={t("outboundOrder.card.fields.destination")} prominent>
                {order.destinationCode}
              </OutboundInfo>
            </Stack>
            <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
              <OutboundInfo label={t("outboundOrder.card.fields.client")}>
                {order.recipientClientCode}
              </OutboundInfo>
              <OutboundInfo label={t("outboundOrder.card.fields.warehouse")}>
                {order.warehouseCode}
              </OutboundInfo>
              <OutboundInfo label={t("outboundOrder.card.fields.lines")}>
                {order.lines.length}
              </OutboundInfo>
              <OutboundInfo label={t("outboundOrder.card.fields.priority")}>
                {order.priority}
              </OutboundInfo>
            </Stack>
            <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
              <OutboundInfo label={t("outboundOrder.card.fields.reference")}>
                {order.externalReference || t("outboundOrder.card.noReference")}
              </OutboundInfo>
              <OutboundInfo label={t("outboundOrder.card.fields.submittedAt")}>
                {new Date(order.submittedAt).toLocaleString(locale(i18n.language))}
              </OutboundInfo>
            </Stack>
          </Stack>
          {internal && ["SUBMITTED", "ATP_FAILED"].includes(order.status)
            && permissions.includes("outbound_order:manage") && (
            <Button variant="contained" startIcon={<CheckCircleOutline />}
              disabled={accept.isPending} onClick={() => accept.mutate()}>
              {order.status === "SUBMITTED"
                ? t("outboundOrder.card.accept")
                : t("outboundOrder.card.retryAtp")}
            </Button>
          )}
          {!internal && order.status === "SUBMITTED"
            && permissions.includes("outbound_order:self:cancel") && (
            <Button color="error" variant="outlined"
              disabled={cancel.isPending} onClick={() => cancel.mutate()}>
              {t("common.actions.cancel")}
            </Button>
          )}
          <Chip label={displayStatus} color={tone} sx={{ minWidth: 148 }} />
          <IconButton onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? t("outboundOrder.card.collapse") : t("outboundOrder.card.expand")}
            aria-expanded={expanded}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Stack>
        {expanded && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            {(!internal || order.status !== "SUBMITTED") && (
              <Box sx={{
                p: { xs: 1.5, md: 2 },
                bgcolor: "background.default",
                borderRadius: 1.5,
                border: 1,
                borderColor: "divider"
              }}>
                <Typography variant="subtitle1" fontWeight={850}
                  sx={{ mb: 1.5 }}>
                  {internal ? t("outboundOrder.card.logistics") : t("outboundOrder.card.tracking")}
                </Typography>
                <Stepper alternativeLabel
                  activeStep={activeMilestoneIndex} sx={{
                    display: { xs: "none", md: "flex" },
                    "& .MuiStepConnector-line": { minHeight: 2 },
                    "& .MuiStepLabel-label": { mt: .5 }
                  }}>
                  {order.milestones.map((milestone) => (
                    <Step key={milestone.code}
                      completed={milestone.state === "COMPLETED"}>
                      <StepLabel error={milestone.state === "ERROR"}
                        optional={(
                          <Typography variant="caption" component="span">
                            {milestoneDetail(milestone)}
                            {milestone.occurredAt && (
                              <><br />{formatMoment(milestone.occurredAt, i18n.language)}</>
                            )}
                            {internal && milestone.references.length > 0 && (
                              <Stack component="span" direction="row" spacing={.5}
                                justifyContent="center" useFlexGap flexWrap="wrap"
                                sx={{ mt: .5 }}>
                                {milestone.references.map((reference) => (
                                  <Chip key={`${reference.type}-${reference.id}`}
                                    component="span" size="small"
                                    color="primary" variant="outlined"
                                    clickable={reference.type !== "OUTBOUND_ORDER"}
                                    onClick={reference.type !== "OUTBOUND_ORDER"
                                      ? () => onOpenReference?.(reference)
                                      : undefined}
                                    label={reference.label} />
                                ))}
                              </Stack>
                            )}
                          </Typography>
                        )}>
                        {milestoneLabel(milestone.code, milestone.label)}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
                <Stack spacing={1} sx={{ display: { xs: "flex", md: "none" } }}>
                  {order.milestones.map((milestone) => (
                    <Stack key={milestone.code} direction="row" spacing={1.25}
                      alignItems="flex-start">
                      <Box sx={{
                        width: 12, height: 12, borderRadius: "50%", mt: .65,
                        flex: "0 0 auto",
                        bgcolor: milestone.state === "COMPLETED"
                          ? "success.main"
                          : milestone.state === "ERROR" ? "error.main"
                            : milestone.state === "CURRENT"
                              ? "warning.main" : "grey.300"
                      }} />
                      <Box>
                        <Typography fontWeight={750}>
                          {milestoneLabel(milestone.code, milestone.label)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {milestoneDetail(milestone)}
                          {milestone.occurredAt && (
                            <> · {formatMoment(milestone.occurredAt, i18n.language)}</>
                          )}
                        </Typography>
                        {internal && milestone.references.length > 0 && (
                          <Stack direction="row" spacing={.5} useFlexGap
                            flexWrap="wrap" sx={{ mt: .5 }}>
                            {milestone.references.map((reference) => (
                              <Chip key={`${reference.type}-${reference.id}`}
                                size="small" color="primary" variant="outlined"
                                clickable={reference.type !== "OUTBOUND_ORDER"}
                                onClick={reference.type !== "OUTBOUND_ORDER"
                                  ? () => onOpenReference?.(reference)
                                  : undefined}
                                label={reference.label} />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
            <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
              <OutboundInfo label={t("outboundOrder.card.fields.destination")}>
                {order.destinationName}
              </OutboundInfo>
              {order.requestedDeliveryFrom && (<OutboundInfo label={t("outboundOrder.card.fields.neededAt")}>{new Date(order.requestedDeliveryFrom).toLocaleString(locale(i18n.language))}</OutboundInfo>)}
            </Stack>
            {order.lines.map((line) => (
              <Box key={line.id} sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr", md: "minmax(0,1fr) auto auto"
                },
                gap: 1, alignItems: "center", p: 1.25,
                border: 1, borderColor: "divider", borderRadius: 1
              }}>
                <Stack spacing={1} sx={{ minWidth: 0 }}>
                  <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <OutboundInfo label={t("outboundOrder.card.fields.line")}>
                      {line.lineNumber}
                    </OutboundInfo>
                    <OutboundInfo label={t("outboundOrder.card.fields.item")} prominent>
                      {line.itemCode}
                    </OutboundInfo>
                    <OutboundInfo label={t("outboundOrder.card.fields.description")} prominent>
                      {line.itemName}
                    </OutboundInfo>
                  </Stack>
                  <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <OutboundInfo label={t("outboundOrder.card.fields.requested")}>
                      {formatQuantity(line.requestedQuantity, i18n.language)} {line.uom}
                    </OutboundInfo>
                    <OutboundInfo label={t("outboundOrder.card.fields.atp")}>
                      {formatQuantity(line.atpQuantity, i18n.language)} {line.uom}
                    </OutboundInfo>
                    <OutboundInfo label={t("outboundOrder.card.fields.remaining")} color={line.requestedQuantity >
                       line.atpQuantity ? "error.main" : "success.main"}>{formatQuantity(Math.max(0, line.requestedQuantity - line.atpQuantity), i18n.language)} {line.uom}
                    </OutboundInfo>
                  </Stack>
                  <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <OutboundInfo label={t("outboundOrder.card.fields.expectedInbound")} color="info.main">
                      {formatQuantity(line.expectedInboundQuantity, i18n.language)} {line.uom}
                    </OutboundInfo>
                    {line.nextExpectedArrivalAt && (<OutboundInfo label={t("outboundOrder.card.fields.nextArrival")} color="info.main">{new Date(line.nextExpectedArrivalAt).toLocaleString(locale(i18n.language))}</OutboundInfo>)}
                  </Stack>
                </Stack>
                {line.status === "ATP_AVAILABLE" && internal
                  && permissions.includes("outbound_order:allocate") && (
                  <Button variant="contained" disabled={allocate.isPending}
                    onClick={() => allocate.mutate(line.id)}>
                    {t("outboundOrder.card.confirmAllocation")}
                  </Button>
                )}
                <Chip label={t(`outboundOrder.status.${[
                  line.status === "ATP_AVAILABLE" ? "ATP_AVAILABLE"
                    : line.status === "ATP_FAILED" ? "ATP_FAILED"
                      : line.status === "ALLOCATED" ? "ALLOCATED"
                        : line.status === "CANCELLED" ? "CANCELLED"
                          : "SUBMITTED"
                ]}`)} size="small" variant="outlined" />
              </Box>
            ))}
            {(accept.isError || allocate.isError || cancel.isError) && (
              <Alert severity="error">
                {(accept.error || allocate.error || cancel.error) instanceof Error
                  ? (accept.error || allocate.error || cancel.error)?.message
                  : t("outboundOrder.errors.action")}
              </Alert>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function CreateOutboundOrderDialog({
  open, onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const catalog = useQuery({
    queryKey: ["portal-outbound-catalog"],
    queryFn: loadPortalOutboundCatalog,
    enabled: open
  });
  const warehouses = catalog.data?.warehouses ?? [];
  const activeClients = catalog.data?.clients ?? [];
  const [warehouseId, setWarehouseId] = useState("");
  const [recipientClientId, setRecipientClientId] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [lines, setLines] = useState<Array<{
    itemId: string;
    quantity: number;
  }>>([]);
  const [reference, setReference] = useState("");
  const [orderType, setOrderType] =
    useState<CreateOutboundOrder["orderType"]>("POU_REQUEST");
  const selectedClient = activeClients.find(
    (client) => client.id === recipientClientId
  );
  const destinations = selectedClient?.destinations ?? [];
  const item = catalog.data?.items.find((product) => product.id === itemId);
  const itemAlreadyAdded = lines.some((line) => line.itemId === itemId);
  const valid = warehouseId && recipientClientId && destinationId
    && lines.length > 0 && lines.every((line) => line.quantity > 0);
  const resetAndClose = () => {
    setWarehouseId("");
    setRecipientClientId("");
    setDestinationId("");
    setItemId("");
    setQuantity(1);
    setLines([]);
    setReference("");
    setOrderType("POU_REQUEST");
    onClose();
  };
  const addLine = () => {
    if (!item || quantity <= 0 || itemAlreadyAdded) return;
    setLines((current) => [...current, { itemId: item.id, quantity }]);
    setItemId("");
    setQuantity(1);
  };
  const create = useMutation({
    mutationFn: () => createPortalOutboundOrder({
      warehouseId,
      ownerClientId: recipientClientId,
      recipientClientId,
      destinationId,
      externalReference: reference.trim() || null,
      orderType,
      requestedDeliveryFrom: null,
      requestedDeliveryTo: null,
      priority: 100,
      routeReference: null,
      instructions: null,
      lines: lines.map((line) => {
        const product = catalog.data!.items.find(
          (candidate) => candidate.id === line.itemId
        )!;
        return {
          itemId: line.itemId,
          quantity: line.quantity,
          uom: product.baseUom
        };
      })
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["outbound-orders"] });
      resetAndClose();
    }
  });

  return (
    <Dialog open={open} onClose={resetAndClose} fullWidth maxWidth="md">
      <DialogTitle>{t("outboundOrder.create.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>{t("outboundOrder.create.warehouse")}</InputLabel>
              <Select value={warehouseId} label={t("outboundOrder.create.warehouse")}
                onChange={(event) => setWarehouseId(event.target.value)}>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} · {warehouse.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t("outboundOrder.create.requestType")}</InputLabel>
              <Select value={orderType} label={t("outboundOrder.create.requestType")}
                onChange={(event) => setOrderType(
                  event.target.value as CreateOutboundOrder["orderType"]
                )}>
                <MenuItem value="POU_REQUEST">{t("outboundOrder.create.types.POU_REQUEST")}</MenuItem>
                <MenuItem value="CUSTOMER_ORDER">{t("outboundOrder.create.types.CUSTOMER_ORDER")}</MenuItem>
                <MenuItem value="STOCK_TRANSFER">{t("outboundOrder.create.types.STOCK_TRANSFER")}</MenuItem>
                <MenuItem value="EMERGENCY">{t("outboundOrder.create.types.EMERGENCY")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <FormControl fullWidth>
            <InputLabel>{t("outboundOrder.create.recipient")}</InputLabel>
            <Select value={recipientClientId} label={t("outboundOrder.create.recipient")}
              onChange={(event) => {
                setRecipientClientId(event.target.value);
                setDestinationId("");
              }}>
              {activeClients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.code} · {client.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={!recipientClientId}>
            <InputLabel>{t("outboundOrder.create.destination")}</InputLabel>
            <Select value={destinationId} label={t("outboundOrder.create.destination")}
              onChange={(event) => setDestinationId(event.target.value)}>
              {destinations.map((destination) => (
                <MenuItem key={destination.id} value={destination.id}>
                  {destination.code} · {destination.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="subtitle1" fontWeight={800}>
            {t("outboundOrder.create.products", { count: lines.length })}
          </Typography>
          {lines.map((line, index) => {
            const product = catalog.data?.items.find(
              (candidate) => candidate.id === line.itemId
            );
            return (
              <Stack key={line.itemId} direction="row" spacing={1.5}
                alignItems="center" sx={{
                  p: 1.25, border: 1, borderColor: "divider",
                  borderRadius: 1
                }}>
                <Typography sx={{ minWidth: 34 }} fontWeight={750}>
                  {index * 10 + 10}.
                </Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography fontWeight={750}>
                    {product?.code} · {product?.displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("outboundOrder.create.unit", { uom: product?.baseUom })}
                  </Typography>
                </Box>
                <TextField label={t("outboundOrder.create.quantity")} type="number" size="small"
                  value={line.quantity} sx={{ width: 150 }}
                  onChange={(event) => {
                    const nextQuantity = Number(event.target.value);
                    setLines((current) => current.map((candidate) =>
                      candidate.itemId === line.itemId
                        ? { ...candidate, quantity: nextQuantity }
                        : candidate
                    ));
                  }}
                  slotProps={{ htmlInput: { min: 1 } }} />
                <IconButton color="error" aria-label={t("outboundOrder.create.deleteLine")}
                  onClick={() => setLines((current) => current.filter(
                    (candidate) => candidate.itemId !== line.itemId
                  ))}>
                  <DeleteOutline />
                </IconButton>
              </Stack>
            );
          })}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}
            alignItems={{ md: "center" }}>
            <FormControl fullWidth>
              <InputLabel>{t("outboundOrder.create.item")}</InputLabel>
              <Select value={itemId} label={t("outboundOrder.create.item")}
                onChange={(event) => setItemId(event.target.value)}>
                {catalog.data?.items.map((product) => (
                  <MenuItem key={product.id} value={product.id}
                    disabled={lines.some((line) =>
                      line.itemId === product.id
                    )}>
                    {product.code} · {product.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label={t("outboundOrder.create.quantity")} type="number" value={quantity}
              onChange={(event) => setQuantity(Number(event.target.value))}
              slotProps={{ htmlInput: { min: 1 } }} />
            <Button variant="outlined" startIcon={<AddOutlined />}
              sx={{ minWidth: 180, alignSelf: { md: "stretch" } }}
              disabled={!item || quantity <= 0 || itemAlreadyAdded}
              onClick={addLine}>
              {t("outboundOrder.create.add")}
            </Button>
          </Stack>
          <TextField label={t("outboundOrder.create.reference")}
            value={reference}
            onChange={(event) => setReference(event.target.value)} />
          {create.isError && (
            <Alert severity="error">
              {create.error instanceof Error
                ? create.error.message : t("outboundOrder.errors.create")}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>{t("common.actions.cancel")}</Button>
        <Button variant="contained" disabled={!valid || create.isPending}
          onClick={() => create.mutate()}>
          {t("outboundOrder.create.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
